import { useEffect, useRef, useState, type FormEvent, type MouseEvent } from "react";
import {
  submitFeedbackIssue,
  type CreatedFeedbackIssue,
  type FeedbackType,
} from "./api";

const TYPE_OPTIONS: Array<{ value: FeedbackType; label: string }> = [
  { value: "bug", label: "不具合" },
  { value: "request", label: "改善要望" },
  { value: "question", label: "問い合わせ" },
];

export default function FeedbackWidget() {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState<FeedbackType>("bug");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [reproduction, setReproduction] = useState("");
  const [expected, setExpected] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdIssue, setCreatedIssue] = useState<CreatedFeedbackIssue | null>(null);
  const titleRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    titleRef.current?.focus();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !submitting) setOpen(false);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, submitting]);

  const reset = () => {
    setType("bug");
    setTitle("");
    setDescription("");
    setReproduction("");
    setExpected("");
    setError(null);
    setCreatedIssue(null);
  };

  const close = () => {
    if (submitting) return;
    setOpen(false);
    reset();
  };

  const handleBackdropClick = (event: MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) close();
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const issue = await submitFeedbackIssue({
        type,
        title,
        description,
        ...(reproduction.trim() ? { reproduction } : {}),
        ...(expected.trim() ? { expected } : {}),
        pageUrl: `${window.location.origin}${window.location.pathname}${window.location.search}`,
        userAgent: navigator.userAgent,
      });
      setCreatedIssue(issue);
    } catch {
      setError("送信できませんでした。時間をおいてもう一度お試しください。");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className="feedback-trigger"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
      >
        不具合・ご意見
      </button>

      {open && (
        <div className="modal-backdrop" onMouseDown={handleBackdropClick}>
          <section
            className="feedback-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="feedback-title"
          >
            <div className="modal-heading">
              <div>
                <h2 id="feedback-title">不具合・ご意見を送る</h2>
                <p>内容は担当チームのGitHub Issueに登録されます。</p>
              </div>
              <button
                type="button"
                className="modal-close"
                aria-label="閉じる"
                disabled={submitting}
                onClick={close}
              >
                ×
              </button>
            </div>

            {createdIssue ? (
              <div className="feedback-success" role="status">
                <strong>送信しました</strong>
                <p>
                  Issue #{createdIssue.number} を登録しました。ご報告ありがとうございます。
                </p>
                <div className="modal-actions">
                  <button type="button" onClick={close}>閉じる</button>
                  <a
                    className="nav-link"
                    href={createdIssue.url}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Issueを確認
                  </a>
                </div>
              </div>
            ) : (
              <form className="feedback-form" onSubmit={(event) => void handleSubmit(event)}>
                <label>
                  種別
                  <select
                    value={type}
                    disabled={submitting}
                    onChange={(event) => setType(event.target.value as FeedbackType)}
                  >
                    {TYPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </label>
                <label>
                  件名 <span aria-hidden="true">*</span>
                  <input
                    ref={titleRef}
                    required
                    maxLength={120}
                    value={title}
                    disabled={submitting}
                    placeholder="例：予定を保存しても画面に反映されない"
                    onChange={(event) => setTitle(event.target.value)}
                  />
                </label>
                <label>
                  内容 <span aria-hidden="true">*</span>
                  <textarea
                    required
                    rows={5}
                    maxLength={5000}
                    value={description}
                    disabled={submitting}
                    placeholder="発生したことを具体的にご記入ください"
                    onChange={(event) => setDescription(event.target.value)}
                  />
                </label>
                <label>
                  再現手順 <span className="optional">任意</span>
                  <textarea
                    rows={3}
                    maxLength={3000}
                    value={reproduction}
                    disabled={submitting}
                    placeholder="1. ○○を開く  2. △△を押す"
                    onChange={(event) => setReproduction(event.target.value)}
                  />
                </label>
                <label>
                  期待する動作 <span className="optional">任意</span>
                  <textarea
                    rows={2}
                    maxLength={3000}
                    value={expected}
                    disabled={submitting}
                    onChange={(event) => setExpected(event.target.value)}
                  />
                </label>
                <p className="feedback-note">
                  送信時にMicrosoft 365のアカウント、対象ページ、ブラウザ情報を添付します。
                </p>
                {error && <p className="modal-error" role="alert">{error}</p>}
                <div className="modal-actions">
                  <button type="button" disabled={submitting} onClick={close}>キャンセル</button>
                  <button
                    type="submit"
                    className="primary-action"
                    disabled={submitting || !title.trim() || !description.trim()}
                  >
                    {submitting ? "送信中…" : "送信する"}
                  </button>
                </div>
              </form>
            )}
          </section>
        </div>
      )}
    </>
  );
}
